import React from 'react';

import {
  AttachFile as AttachFileIcon,
  Close as CloseIcon,
  DescriptionOutlined as DescriptionOutlinedIcon,
} from '@mui/icons-material';
import { Box, CircularProgress, FormHelperText, IconButton, Link, Typography } from '@mui/material';
import _ from 'es-toolkit/compat';
import { FieldRenderProps, useForm, useFormState } from 'react-final-form';

import { filePickerFileTypes } from '@clearverify/constants';

import { FileResponseInfoFragment } from 'src/api';
import { FileUploadField } from 'src/components/finalForm';
import { DEFAULT_FILE_TYPES } from 'src/constants/file';
import { useScreenInfo } from 'src/hooks/common';
import { useFileUpload } from 'src/hooks/file';
import { FilePickerFileTypesKeys } from 'src/types/file';
import { SxProp } from 'src/types/mui';
import { convertFileSize } from 'src/utils/file';

type AttachFilesUploadFieldProps = FieldRenderProps<FileResponseInfoFragment[]> & {
  maxFiles?: number;
  disabled?: boolean;
  acceptFileTypes?: Array<FilePickerFileTypesKeys>;
  hideAttachedFiles?: boolean;
};

export const AttachFilesUploadField: React.FC<AttachFilesUploadFieldProps> = ({
  meta,
  input,
  maxFiles = 5,
  disabled = false,
  acceptFileTypes = DEFAULT_FILE_TYPES,
  hideAttachedFiles = false,
}) => {
  const { isMobile } = useScreenInfo();
  const { uploadFiles, uploading } = useFileUpload();

  const { change } = useForm();

  const { submitFailed } = useFormState();

  const { value: files, name: fieldName } = input;
  const isInvalid = (meta.invalid && meta.modified) || (submitFailed && meta.invalid);
  const errorText = isInvalid ? meta.error : undefined;

  const onDeleteByFileId = (fileId: string) => {
    change(
      fieldName,
      files.filter(file => file.id !== fileId),
    );
  };

  const acceptFiles = React.useMemo(
    () =>
      acceptFileTypes.reduce(
        (acc, fileType: FilePickerFileTypesKeys) => ({ ...acc, ...filePickerFileTypes[fileType] }),
        {},
      ),
    [acceptFileTypes],
  );

  const renderAttachField = React.useCallback(() => {
    return (
      <FileUploadField
        name={fieldName}
        onChange={async files => {
          try {
            const uploadedFiles = await uploadFiles(files.map(fileItem => ({ file: fileItem })));

            if (maxFiles === 1) {
              change(fieldName, uploadedFiles);
            } else {
              change(fieldName, [...files, ...uploadedFiles]);
            }
          } catch (error) {
            console.error(error);
            change(fieldName, undefined);
          }
        }}
        accept={acceptFiles}
        filePickerSx={{ width: '100%' }}
        disabled={disabled || uploading}
        maxFiles={maxFiles}
      >
        <IconButton>
          <AttachFileIcon />
        </IconButton>
        {!uploading && isInvalid && errorText && (
          <FormHelperText error={isInvalid}>{errorText}</FormHelperText>
        )}
      </FileUploadField>
    );
  }, [
    acceptFiles,
    change,
    disabled,
    errorText,
    fieldName,
    isInvalid,
    maxFiles,
    uploadFiles,
    uploading,
  ]);

  if (uploading) {
    return <CircularProgress color="inherit" size="1em" />;
  }

  return (
    <Box>
      {isMobile && (
        <Box sx={attachFileOuterBoxStyles}>
          <Box sx={{ opacity: '0', height: '50px' }} />
          {renderAttachField()}
        </Box>
      )}

      <Box sx={attachFileOuterBoxStyles}>
        {files.length && !_.isNil(files[0]) && !hideAttachedFiles ? (
          <Box sx={filesListStyles} pr={isMobile ? 0 : 4}>
            {files.map(file => (
              <Box key={file.id}>
                <Box sx={fileContainerStyles}>
                  <Box sx={fileContainerInnerStyles}>
                    <Box sx={fileIconWrapperStyles}>
                      <DescriptionOutlinedIcon
                        style={{ margin: '0 10px 0 0', color: '#7d99d7' }}
                        fontSize="large"
                      />
                    </Box>
                    <Box>
                      <Typography
                        sx={fileNameStyles}
                        align="inherit"
                        color="initial"
                        paragraph={false}
                      >
                        <Link
                          href={file.downloadUrl || ''}
                          underline="none"
                          component="a"
                          target="_blank"
                        >
                          {file.filename}
                        </Link>
                      </Typography>

                      <Typography
                        sx={fileSizeStyles}
                        align="inherit"
                        color="initial"
                        paragraph={false}
                      >
                        {convertFileSize(file.size)} Document
                      </Typography>
                    </Box>
                  </Box>
                  <Box>
                    <IconButton
                      sx={{ paddingLeft: '5px' }}
                      color="default"
                      size="small"
                      onClick={() => onDeleteByFileId(file.id)}
                      disabled={disabled || uploading}
                    >
                      <CloseIcon color="error" />
                    </IconButton>
                  </Box>
                </Box>
              </Box>
            ))}
          </Box>
        ) : (
          <Box sx={{ opacity: '0' }} />
        )}
        {!isMobile ? renderAttachField() : null}
      </Box>
    </Box>
  );
};

const filesListStyles: SxProp = {
  display: 'flex',
  flexDirection: 'column',
  rowGap: '8px',
  width: '100%',
};

const fileContainerInnerStyles: SxProp = {
  display: 'grid',
  gridTemplateColumns: '50px max-content',
  alignItems: 'center',
};

const fileContainerStyles: SxProp = {
  display: 'grid',
  gridTemplateColumns: 'max-content max-content',
  alignItems: 'center',
  justifyContent: 'space-between',
  border: '1.5px solid #d0d0d0',
  borderRadius: '6px',
  padding: '10px 12px',
};

const fileNameStyles: SxProp = {
  fontWeight: '500',
  fontSize: '16px',
  lineHeight: '16px',
};

const fileSizeStyles: SxProp = {
  fontSize: '16px',
  lineHeight: '20px',
};

const fileIconWrapperStyles: SxProp = {
  display: 'flex',
  alignItems: 'center',
};

const attachFileOuterBoxStyles: SxProp = {
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  width: '100%',
};
